import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import logo from "../../resource/7.png";
import CommonFormFields from "./CommonFormFields";
import Swal from "sweetalert2";
import axios from "axios";
import apiClient from "@/api/client";

const GilanPasaRequestModal = ({ isOpen, onClose, selectedDate, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    wtNumber: "",
    email: "",
    date: "",
    ip_address: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [duplicateError, setDuplicateError] = useState(null);

  useEffect(() => {
    if (selectedDate) {
      setFormData((prev) => ({
        ...prev,
        date: selectedDate,
      }));
      setDuplicateError(null);
    }
  }, [selectedDate]);

  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setFormData((prevFormData) => ({
          ...prevFormData,
          ip_address: response.data.ip,
        }));
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };
    fetchIP();
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (duplicateError) setDuplicateError(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = "First Name is required";
    else if (!/^[A-Za-z\s\-]+$/.test(formData.firstName))
      newErrors.firstName = "First Name should contain only letters, spaces, or hyphens";

    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    else if (!/^[A-Za-z\s\-]+$/.test(formData.lastName))
      newErrors.lastName = "Last Name should contain only letters, spaces, or hyphens";

    if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile Number is required";
    else if (!/^\d{10}$/.test(formData.mobileNumber))
      newErrors.mobileNumber = "Mobile Number should be 10 digits";

    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";

    if (!formData.wtNumber) newErrors.wtNumber = "WhatsApp Number is required";
    else if (!/^\d{10}$/.test(formData.wtNumber))
      newErrors.wtNumber = "WhatsApp Number should be 10 digits";

    if (!formData.date) newErrors.date = "Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please check the form for errors.",
      });
      return;
    }

    setLoading(true);
    setDuplicateError(null);

    try {
      const submissionData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNumber: formData.mobileNumber,
        wtNumber: formData.wtNumber,
        email: formData.email,
        date: formData.date,
        ip_address: formData.ip_address,
      };

      const response = await apiClient.post("/gilanpasa-request", submissionData);

      // Success alert + refresh handled by parent (onSuccess)
      if (onSuccess) onSuccess();
      onClose();

      // Reset form (keep IP)
      setFormData({
        firstName: "",
        lastName: "",
        mobileNumber: "",
        wtNumber: "",
        email: "",
        date: "",
        ip_address: formData.ip_address,
      });
    } catch (error) {
      console.error("Error submitting form:", error);

      let errorMessage = "Something went wrong!";
      let errorTitle = "Oops...";

      if (error.response && error.response.status === 422) {
        const errorData = error.response.data;

        if (errorData.errors) {
          // Laravel validation errors
          const backendErrors = {};
          Object.keys(errorData.errors).forEach((key) => {
            const fieldMap = {
              firstName: "firstName",
              lastName: "lastName",
              mobileNumber: "mobileNumber",
              email: "email",
              date: "date",
              wtNumber: "wtNumber",
            };
            const frontendField = fieldMap[key] || key;
            backendErrors[frontendField] = errorData.errors[key][0];
          });
          setErrors((prev) => ({ ...prev, ...backendErrors }));
          errorMessage = "Please check the form for errors.";
          Swal.fire({ icon: "error", title: "Validation Error", text: errorMessage });
        } else {
          // Business-logic 422 (blocked / already-booked date)
          errorMessage = errorData.message || "This date is not available.";
          errorTitle = "Date Unavailable";
          setDuplicateError(errorMessage);

          Swal.fire({
            icon: "warning",
            title: errorTitle,
            text: errorMessage,
            confirmButtonText: "Close",
            confirmButtonColor: "#c49b36",
          });

          if (onError) onError(errorMessage);
        }
      } else if (error.response) {
        errorMessage = error.response.data.message || "An error occurred while submitting the form.";
        Swal.fire({ icon: "error", title: "Error", text: errorMessage });
      } else if (error.request) {
        errorMessage = "No response from the server. Please check your internet connection and try again.";
        Swal.fire({ icon: "error", title: "Connection Error", text: errorMessage });
      } else {
        Swal.fire({ icon: "error", title: "Oops...", text: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1040,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "700px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
            padding: "15px 20px",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            zIndex: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="form-title" style={{ flex: 1 }}>
            <img src={logo} alt="" width={150} />
            <div className="details">
              <h1 className="font-second" style={{ fontSize: "18px", margin: "5px 0" }}>
                Bhaddeka Vihari Monastery Center
              </h1>
              <h2 className="h4" style={{ fontSize: "16px", margin: 0 }}>
                Gilan Pasa Request Form
              </h2>
            </div>
          </div>
          <button
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",
              color: "#666",
              padding: "0 8px",
              marginLeft: "10px",
              lineHeight: 1,
            }}
            onClick={onClose}
            onMouseEnter={(e) => (e.target.style.color = "#000")}
            onMouseLeave={(e) => (e.target.style.color = "#666")}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            padding: "20px",
            flex: 1,
          }}
        >
          <div
            style={{
              paddingLeft: "12px",
              paddingRight: "12px",
              width: "85%",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {/* Info box — date only, no meal type */}
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "4px",
                marginBottom: "12px",
              }}
            >
              <p className="font-p" style={{ margin: "5px 0" }}>
                <span style={{ fontWeight: "normal" }}>Selected Date:</span>{" "}
                <span style={{ fontWeight: "bold" }}>{selectedDate}</span>
              </p>
            </div>

            {duplicateError && (
              <div
                style={{
                  backgroundColor: "#ffebee",
                  border: "2px solid #f44336",
                  borderRadius: "4px",
                  padding: "12px",
                  marginBottom: "16px",
                  color: "#c62828",
                  textAlign: "center",
                }}
              >
                <strong>⚠️ {duplicateError}</strong>
                <p style={{ fontSize: "12px", marginTop: "5px", marginBottom: "0" }}>
                  Please close this modal and select another date from the calendar.
                </p>
              </div>
            )}

            <p className="font-p" style={{ marginBottom: "0", marginTop: "0" }}>
              Please fill in the following details and we will contact you.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ width: "100%", overflow: "hidden", marginTop: "-12px" }}>
              <CommonFormFields
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />
            </div>

            <button
              type="submit"
              id="submitbtn"
              disabled={loading}
              style={{
                marginTop: "20px",
                width: "100%",
                padding: "12px",
                backgroundColor: "#c49b36",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "16px",
                fontWeight: "bold",
              }}
              onMouseEnter={(e) => {
                if (!loading) e.target.style.backgroundColor = "#b08a2f";
              }}
              onMouseLeave={(e) => {
                if (!loading) e.target.style.backgroundColor = "#c49b36";
              }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </button>

            <p className="font-p" style={{ marginTop: "15px", textAlign: "center" }}>
              Thank you for your kind support. We will be in touch with you shortly.
            </p>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default GilanPasaRequestModal;